import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../../../redux/store';
import { listenToShortstayData, upsertShortstayVideo } from '../../../../redux/shortstaysSlice';

import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const ShortForm6: React.FC = () => {
  const { userId, listingId } = useParams<{ userId: string; listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const currentListing = useSelector((state: RootState) => state.shortstays.currentListing);

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Ref to hidden file input
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (listingId) {
      dispatch(listenToShortstayData(listingId));
    }
  }, [dispatch, listingId]);

  useEffect(() => {
    if (currentListing?.videoUrl) {
      setVideoUrl(currentListing.videoUrl);
    }
  }, [currentListing]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
    } else {
      alert('Please select a valid video file.');
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

const uploadVideo = async () => {
  if (!videoFile || !listingId) return;
  setIsUploading(true);

  try {
    const url = await dispatch(
      upsertShortstayVideo({ listingId, video: videoFile }) // ✅ use "video" not "file"
    ).unwrap();

    setVideoUrl(url);
    setVideoFile(null);
    setSuccess(true);
  } catch (err) {
    alert("❌ Upload failed");
  } finally {
    setIsUploading(false);
    setTimeout(() => setSuccess(false), 1500);
  }
};


  const handleNext = () => {
    navigate(`/shortform7/${userId}/${listingId}`);
  };

  const handleBack = () => {
    navigate(`/shortform5/${userId}/${listingId}`);
  };

  const isNextDisabled = !videoUrl;

  return (
    <div style={styles.pageWrapper}>
      {(isUploading || success) && (
        <div style={styles.overlay}>
          {isUploading && <CircularProgress size={60} sx={{ color: '#fff' }} />}
          {success && <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'limegreen' }} />}
        </div>
      )}

      <div style={styles.container}>
        <h2 style={styles.title}>Upload a 1-Minute Intro Video</h2>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {/* Video upload placeholder or preview */}
        {!videoUrl && !videoFile ? (
          <div style={styles.uploadPlaceholder} onClick={openFilePicker} role="button" tabIndex={0} aria-label="Upload video">
            <CameraAltIcon sx={{ fontSize: 60, color: '#666' }} />
            <AddCircleOutlineIcon sx={{ fontSize: 36, color: '#bf9b30', position: 'absolute', bottom: 8, right: 8 }} />
            <span style={styles.placeholderText}>Click to add video</span>
          </div>
        ) : null}

        {videoUrl && !videoFile && (
          <video controls style={styles.video}>
            <source src={videoUrl} />
            Your browser does not support the video tag.
          </video>
        )}

        {videoFile && (
          <video controls style={styles.video} src={URL.createObjectURL(videoFile)} />
        )}

        {videoFile && (
          <button
            onClick={uploadVideo}
            style={styles.uploadBtn}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload Video'}
          </button>
        )}

        <footer style={styles.footer}>
          <button onClick={handleBack} style={styles.backBtn}>Back</button>
          <button
            onClick={handleNext}
            style={isNextDisabled ? styles.disabledBtn : styles.nextBtn}
            disabled={isNextDisabled}
          >
            Next
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ShortForm6;

const styles: { [key: string]: React.CSSProperties } = {
  pageWrapper: {
    backgroundColor: '#000',
    minHeight: '100vh',
    width: '100vw',
    color: '#fff',
    paddingBottom: 120,
    boxSizing: 'border-box',
    position: 'relative',
  },
  container: {
    maxWidth: 800,
    margin: '0 auto',
    padding: '2rem 1rem',
    boxSizing: 'border-box',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
  },
  uploadPlaceholder: {
    position: 'relative',
    width: 260,
    height: 260,
    margin: '20px auto',
    borderRadius: 20,
    backgroundColor: '#111',
    border: '2px dashed #555',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#666',
    cursor: 'pointer',
    userSelect: 'none',
    flexDirection: 'column',
  },
  placeholderText: {
    marginTop: 12,
    fontSize: 14,
    color: '#999',
  },
  video: {
    marginTop: 20,
    width: '100%',
    maxHeight: 400,
    borderRadius: 12,
    objectFit: 'cover',
  },
  uploadBtn: {
    marginTop: 20,
    background: 'linear-gradient(to right, #fff, #bf9b30)',
    padding: '10px 20px',
    border: 'none',
    borderRadius: 30,
    fontWeight: 'bold',
    color: '#000',
    cursor: 'pointer',
  },
  disabledBtn: {
    marginTop: 20,
    background: '#333',
    padding: '10px 20px',
    border: 'none',
    borderRadius: 30,
    fontWeight: 'bold',
    color: '#999',
    cursor: 'not-allowed',
  },
  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '20px 30px',
    backgroundColor: '#000',
    borderTop: '1px solid #333',
    display: 'flex',
    justifyContent: 'space-between',
    zIndex: 1000,
  },
  backBtn: {
    backgroundColor: '#222',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: 30,
    border: '1px solid #555',
    cursor: 'pointer',
  },
  nextBtn: {
    background: 'linear-gradient(to right, #fff, #bf9b30)',
    padding: '10px 20px',
    borderRadius: 30,
    border: 'none',
    fontWeight: 'bold',
    color: '#000',
    cursor: 'pointer',
  },
};
